import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createSubscriptionPlan,
  ERROR_MESSAGES,
} from '@/epics/subscriptionManagment/api';
import { SUBSCRIPTION_ERROR_MESSAGES } from '@/epics/subscriptionManagment/errors';

// ============================================================================
// Constants
// ============================================================================

export const PERIOD_TYPE_OPTIONS = [
  { id: 'Miesięczny', label: 'Miesięczny' },
  { id: 'Roczny', label: 'Roczny' },
];

export const PERIOD_OPTIONS_MONTHLY = [
  { id: '1 miesiąc', label: '1 miesiąc' },
  { id: '2 miesiące', label: '2 miesiące' },
  { id: '3 miesiące', label: '3 miesiące' },
  { id: '4 miesiące', label: '4 miesiące' },
  { id: '5 miesiące', label: '5 miesiące' },
  { id: '6 miesiące', label: '6 miesiące' },
  { id: '7 miesiące', label: '7 miesiące' },
  { id: '8 miesiące', label: '8 miesiące' },
  { id: '9 miesiące', label: '9 miesiące' },
  { id: '10 miesiące', label: '10 miesiące' },
  { id: '11 miesiące', label: '11 miesiące' },
  { id: '12 miesiące', label: '12 miesiące' },
];

export const PERIOD_OPTIONS_YEARLY = [
  { id: '1 rok', label: '1 rok' },
  { id: '2 lata', label: '2 lata' },
  { id: '3 lata', label: '3 lata' },
  { id: '4 lata', label: '4 lata' },
  { id: '5 lat', label: '5 lat' },
  { id: '6 lat', label: '6 lat' },
  { id: '7 lat', label: '7 lat' },
  { id: '8 lat', label: '8 lat' },
  { id: '9 lat', label: '9 lat' },
  { id: '10 lat', label: '10 lat' },
];

const CYCLE_OPTIONS_MONTHLY = [{ id: 'Miesięczny', label: 'Miesięczny' }];
const CYCLE_OPTIONS_QUARTERLY = [
  { id: 'Miesięczny', label: 'Miesięczny' },
  { id: 'kwartalny', label: 'Kwartalny' },
];

const CYCLE_OPTIONS_YEARLY = [
  { id: 'Miesięczny', label: 'Miesięczny' },
  { id: 'kwartalny', label: 'Kwartalny' },
  { id: 'Roczny', label: 'Roczny' },
];

// ============================================================================
// Types
// ============================================================================

export interface CreateSubscriptionFormData {
  planName: string;
  description: string;
  price: string;
  userCount: string;
  facilityCount: string;
  qmsDocCount: string;
}

export type FormErrors = {
  planName?: string;
  description?: string;
  durationType?: string;
  period?: string;
  cycle?: string;
  price?: string;
  userCount?: string;
  facilityCount?: string;
};

export type CreateSubscriptionFormProps = {
  formData: CreateSubscriptionFormData;
  setFormData: React.Dispatch<React.SetStateAction<CreateSubscriptionFormData>>;
  durationType: string;
  setDurationType: (id: string) => void;
  period: string;
  setPeriod: (id: string) => void;
  cycle: string;
  setCycle: (id: string) => void;
  cycleOptions: { id: string; label: string }[];
  errors: FormErrors;
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  formatPriceWithSpaces: (price: string) => string;
  clampPriceToMax: (value: string) => string;
  maxPriceFormatted: string;
  getBillingDescription: (
    price: string,
    cycle: string,
    period: string,
    formatPriceWithSpacesFn: (p: string) => string
  ) => string;
  isSubmitting: boolean;
  isPreviewStep: boolean;
};

// ============================================================================
// Initial state
// ============================================================================

export const INITIAL_FORM_DATA: CreateSubscriptionFormData = {
  planName: '',
  description: '',
  price: '',
  userCount: '',
  facilityCount: '',
  qmsDocCount: '',
};

export const MAX_PRICE = Number.MAX_SAFE_INTEGER; // 9_007_199_254_740_991

// ============================================================================
// Helper functions
// ============================================================================

export function getCycleOptions(
  durationType: string,
  period: string
): { id: string; label: string }[] {
  if (durationType === 'Roczny') {
    return CYCLE_OPTIONS_YEARLY;
  }
  if (durationType === 'Miesięczny') {
    if (
      period === '1 miesiąc' ||
      period === '2 miesiące' ||
      period === '3 miesiące' ||
      period === '4 miesiące' ||
      period === '5 miesiące'
    ) {
      return CYCLE_OPTIONS_MONTHLY;
    }
    if (period === '6 miesiące') {
      return CYCLE_OPTIONS_QUARTERLY;
    }
    if (period === '12 miesiące') {
      return CYCLE_OPTIONS_YEARLY;
    }
    return CYCLE_OPTIONS_QUARTERLY;
  }
  return [];
}

/** Format price with space as thousand separator and comma as decimal: 1234567.89 → "1 234 567,89" */
export function formatPriceWithSpaces(price: string): string {
  if (!price) {
    return price;
  }
  const stripped = price.replace(/\s/g, '');
  const [intPart = '', decPart] = stripped.split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return decPart !== undefined ? `${formattedInt},${decPart}` : formattedInt;
}

/** Clamp price string to max value (max 2 decimal places). */
export function clampPriceToMax(value: string): string {
  if (!value.replace(/\s/g, '')) {
    return value;
  }
  const num = parseFloat(value.replace(/\s/g, '').replace(',', '.'));
  if (Number.isNaN(num) || num <= MAX_PRICE) {
    return value;
  }
  return String(MAX_PRICE);
}

/** Map billing cycle id to English label for the billing sentence */
export function cycleToEnglish(cycle: string): string {
  switch (cycle) {
    case 'Miesięczny':
      return 'monthly';
    case 'kwartalny':
      return 'quarterly';
    case 'Roczny':
      return 'yearly';
    default:
      return cycle;
  }
}

/** Map billing cycle id to Polish label for the billing sentence (e.g. "co miesiąc", "co roku") */
function cycleToPolish(cycle: string): string {
  switch (cycle) {
    case 'Miesięczny':
      return 'co miesiąc';
    case 'kwartalny':
      return 'co kwartał';
    case 'Roczny':
      return 'co roku';
    default:
      return cycle;
  }
}

/** Map period id to English "X month(s)" or "X year(s)" for the billing sentence */
export function periodToEnglish(period: string): string {
  if (period === '1 miesiąc') {
    return '1 month';
  }
  if (/^\d+ miesiące$/.test(period)) {
    const n = period.replace(/\s*miesiące$/, '');
    return `${n} months`;
  }
  if (period === '1 rok') {
    return '1 year';
  }
  if (/^\d+ lata$/.test(period)) {
    const n = period.replace(/\s*lata$/, '');
    return `${n} years`;
  }
  if (/^\d+ lat$/.test(period)) {
    const n = period.replace(/\s*lat$/, '');
    return `${n} years`;
  }
  return period;
}

/** Build billing description sentence in Polish (e.g. "Za 1 miesiąc zostanie naliczona opłata 1 499,00 zł." or "Przez 2 lata opłata 1 499,00 zł będzie naliczana co roku.") */
export function getBillingDescription(
  price: string,
  cycle: string,
  period: string,
  formatPriceWithSpacesFn: (p: string) => string
): string {
  const hasPrice = price != null && price.replace(/\s/g, '').length > 0;
  const hasCycle = cycle != null && cycle.length > 0;
  const hasPeriod = period != null && period.length > 0;

  if (hasPrice && hasCycle && hasPeriod) {
    let formatted =
      Number(price) > 0
        ? formatPriceWithSpacesFn(price).replace('.', ',')
        : '0,00';
    if (!formatted.includes(',')) {
      formatted = `${formatted},00`;
    }
    const cyclePl = cycleToPolish(cycle);

    if (period === '1 miesiąc') {
      return `Za 1 miesiąc zostanie naliczona opłata ${formatted} zł.`;
    }
    return `Przez ${period} opłata ${formatted} zł będzie naliczana ${cyclePl}.`;
  }

  return 'Opłata zostanie naliczona zgodnie z wybranym cyklem rozliczeniowym dla wybranego okresu.';
}

// ============================================================================
// Hook
// ============================================================================

export function useCreateSubscription() {
  const navigate = useNavigate();

  // Form state
  const [durationType, setDurationType] = useState<string>('');
  const [period, setPeriod] = useState<string>('');
  const [cycle, setCycle] = useState<string>('');
  const [formData, setFormData] =
    useState<CreateSubscriptionFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});

  // UI state
  const [isPreviewStep, setIsPreviewStep] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // Derived
  const cycleOptions = getCycleOptions(durationType, period);

  // Reset period and cycle when period type changes (options change)
  useEffect(() => {
    setPeriod('');
    setCycle('');
  }, [durationType]);

  // Reset cycle when period changes (cycle options may change)
  useEffect(() => {
    setCycle('');
  }, [period]);

  // Auto-fill cycle when there is only one option
  useEffect(() => {
    const options = getCycleOptions(durationType, period);
    const singleOption = options[0];
    if (options.length === 1 && singleOption) {
      setErrors((prev) => ({ ...prev, cycle: '' }));
      setCycle(singleOption.id);
    }
  }, [durationType, period]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const requiredMessage = SUBSCRIPTION_ERROR_MESSAGES.REQUIRED;
      const newErrors: FormErrors = {};

      if (!formData.planName.trim()) {
        newErrors.planName = requiredMessage;
      }
      if (!formData.description.trim()) {
        newErrors.description = requiredMessage;
      }
      if (!durationType) {
        newErrors.durationType = requiredMessage;
      }
      if (!period) {
        newErrors.period = requiredMessage;
      }
      if (!cycle) {
        newErrors.cycle = requiredMessage;
      }
      const priceNum = parseFloat(formData.price.replace(/\s/g, ''));
      if (
        formData.price.trim() === '' ||
        Number.isNaN(priceNum) ||
        priceNum < 0
      ) {
        newErrors.price = requiredMessage;
      } else if (priceNum > Number.MAX_SAFE_INTEGER) {
        newErrors.price = SUBSCRIPTION_ERROR_MESSAGES.PRICE_TOO_BIG;
      }
      if (!formData.userCount.trim()) {
        newErrors.userCount = requiredMessage;
      }
      if (!formData.facilityCount.trim()) {
        newErrors.facilityCount = requiredMessage;
      }

      setErrors(newErrors);
      const hasFieldErrors = Object.keys(newErrors).length > 0;
      if (hasFieldErrors) {
        return;
      }

      // First click: validation passed → go to "preview" step (button becomes "Utwórz plan")
      if (!isPreviewStep) {
        setIsPreviewStep(true);
        return;
      }

      // Second click: call API
      setIsSubmitting(true);
      setErrors({});

      try {
        const result = await createSubscriptionPlan({
          planName: formData.planName.trim(),
          planDescription: formData.description.trim(),
          durationType,
          period,
          cycle,
          price: formData.price,
          ...(formData.userCount && { userCount: formData.userCount }),
          ...(formData.facilityCount && {
            facilityCount: formData.facilityCount,
          }),
          ...(formData.qmsDocCount && { qmsDocCount: formData.qmsDocCount }),
        });

        if (result.success) {
          navigate('/Plany', {
            replace: true,
            state: { showCreatePlanSuccess: true },
          });
          return;
        }

        if (result.fieldErrors && Object.keys(result.fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...result.fieldErrors }));
        }
        const isPlanNameExistsError =
          result.error === ERROR_MESSAGES.PLAN_NAME_ALREADY_EXISTS ||
          result.error
            ?.toLowerCase()
            .includes('subscription plan name already exists');
        if (isPlanNameExistsError) {
          setErrors((prev) => ({
            ...prev,
            planName: SUBSCRIPTION_ERROR_MESSAGES.PLAN_NAME_ALREADY_EXISTS,
          }));
        } else if (result.error) {
          setSnackbarError(result.error);
          setIsSnackbarOpen(true);
        }
      } catch {
        setSnackbarError(ERROR_MESSAGES.UNEXPECTED_ERROR);
        setIsSnackbarOpen(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, durationType, period, cycle, navigate, isPreviewStep]
  );

  const hasContent = Boolean(
    formData.planName ||
    formData.description ||
    formData.price ||
    formData.userCount ||
    formData.facilityCount ||
    formData.qmsDocCount ||
    durationType ||
    period ||
    cycle
  );

  const handleCancel = useCallback(() => {
    if (isPreviewStep) {
      setIsPreviewStep(false);
      return;
    }
    if (!hasContent) {
      navigate('/Plany', { replace: true });
      return;
    }
    setIsCancelModalOpen(true);
  }, [isPreviewStep, hasContent, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate('/Plany', { replace: true });
  }, [navigate]);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
    setSnackbarError('');
  }, []);

  return {
    // Form state
    formData,
    setFormData,
    durationType,
    setDurationType,
    period,
    setPeriod,
    cycle,
    setCycle,
    cycleOptions,
    errors,
    setErrors,

    // UI state
    isPreviewStep,
    isSubmitting,
    isSnackbarOpen,
    snackbarError,
    isCancelModalOpen,

    // Handlers
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,

    // Helpers (for form JSX)
    formatPriceWithSpaces,
    clampPriceToMax,
    getBillingDescription,
    cycleToEnglish,
    periodToEnglish,
  };
}
